import React, { useState, useEffect } from 'react';
import { Modal, Table, Tag, Typography, Spin, Alert, Tooltip } from 'antd';
import { ExperimentOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import moment from 'moment';
import { getLabParameterHistory } from '../utils/abnormalLabsUtils';

const { Title, Text } = Typography;

// Helper function to get display name for lab parameter
const getLabDisplayName = (parameterName) => {
  const labDisplayNames = {
    'labs_lipid_chol_total': 'Total Cholesterol',
    'labs_lipid_hdl': 'HDL Cholesterol',
    'labs_lipid_ldl': 'LDL Cholesterol',
    'labs_lipid_trig': 'Triglycerides',
    'labs_glucose_glucose': 'Glucose',
    'labs_glucose_hba1c': 'HbA1c',
    'labs_cbc_wbc': 'White Blood Cells',
    'labs_cbc_rbc': 'Red Blood Cells',
    'labs_cbc_hgb': 'Hemoglobin',
    'labs_cbc_hct': 'Hematocrit',
    'labs_cbc_plt': 'Platelets',
    'labs_cmp_sodium': 'Sodium',
    'labs_cmp_potassium': 'Potassium',
    'labs_cmp_chloride': 'Chloride',
    'labs_cmp_co2': 'CO2',
    'labs_cmp_bun': 'BUN',
    'labs_cmp_creatinine': 'Creatinine',
    'labs_cmp_glucose': 'Glucose',
    'labs_liver_alt': 'ALT',
    'labs_liver_ast': 'AST',
    'labs_liver_alp': 'Alkaline Phosphatase',
    'labs_liver_bilirubin_total': 'Total Bilirubin',
    'labs_thyroid_tsh': 'TSH',
    'labs_thyroid_t4_free': 'Free T4',
    'labs_thyroid_t3_free': 'Free T3'
  };
  
  return labDisplayNames[parameterName] || (parameterName ? parameterName.replace(/^labs_/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown Parameter');
};

// Helper function to get flag color
const getFlagColor = (flag) => {
  if (!flag) return 'default';
  
  const upperFlag = flag.toString().toUpperCase();
  switch (upperFlag) {
    case 'H':
    case 'HIGH':
      return 'orange';
    case 'L':
    case 'LOW':
      return 'blue';
    case 'C':
    case 'CRITICAL':
      return 'red';
    case 'A':
    case 'ABNORMAL':
      return 'orange';
    case 'N':
    case 'NORMAL':
      return 'green';
    default:
      return 'default';
  }
};

// Helper function to get trend icon
const getTrendIcon = (currentValue, previousValue) => {
  if (!currentValue || !previousValue) return null;
  
  const current = parseFloat(currentValue);
  const previous = parseFloat(previousValue);
  
  if (isNaN(current) || isNaN(previous)) return null;
  
  if (current > previous) {
    return <ArrowUpOutlined style={{ color: '#ff4d4f', marginLeft: 4 }} />;
  } else if (current < previous) {
    return <ArrowDownOutlined style={{ color: '#52c41a', marginLeft: 4 }} />;
  }
  
  return null;
};

const LabHistoryModal = ({
  visible,
  onClose,
  parameterName,
  labEntries = [],
  loading = false
}) => {
  const [historyData, setHistoryData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    if (visible && parameterName && labEntries.length > 0) {
      setDataLoading(true);
      try {
        const history = getLabParameterHistory(labEntries, parameterName, 365); // Last year
        setHistoryData(history);
      } catch (error) {
        console.error('Error processing lab history:', error);
        setHistoryData([]);
      } finally {
        setDataLoading(false);
      }
    }
  }, [visible, parameterName, labEntries]);

  // Safety check for required props - after all hooks
  if (!parameterName) {
    return null;
  }

  const displayName = getLabDisplayName(parameterName);

  const columns = [
    {
      title: 'Date',
      dataIndex: 'entryDate',
      key: 'entryDate',
      width: 120,
      render: (date) => moment(date).format('MM/DD/YYYY'),
      sorter: (a, b) => new Date(b.entryDate) - new Date(a.entryDate),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: 100,
      render: (value, record, index) => {
        const previousValue = historyData[index + 1]?.value;
        const trendIcon = getTrendIcon(value, previousValue);
        
        return (
          <span>
            {value} {record.units}
            {trendIcon}
          </span>
        );
      }
    },
    {
      title: 'Reference Range',
      dataIndex: 'referenceRange',
      key: 'referenceRange',
      width: 150,
      render: (range) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {range || 'N/A'}
        </Text>
      )
    },
    {
      title: 'Flag',
      dataIndex: 'flag',
      key: 'flag',
      width: 80,
      render: (flag, record) => {
        if (!flag) return null;
        
        return (
          <Tag color={getFlagColor(flag)} size="small">
            {flag}
          </Tag>
        );
      }
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (source) => (
        <Text type="secondary" style={{ fontSize: '11px' }}>
          {source || 'Lab'}
        </Text>
      )
    }
  ];

  const abnormalCount = historyData.filter(item => item.isAbnormal).length;
  const normalCount = historyData.length - abnormalCount;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ExperimentOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          <span>{displayName} - Historical Values</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      style={{ top: 20 }}
    >
      {(loading || dataLoading) ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Loading lab history...</Text>
          </div>
        </div>
      ) : historyData.length === 0 ? (
        <Alert
          message="No Historical Data"
          description="No historical lab values found for this parameter in the last year."
          type="info"
          showIcon
        />
      ) : (
        <>
          {/* Summary */}
          <div style={{ 
            marginBottom: 16, 
            padding: '12px 16px', 
            backgroundColor: '#f5f5f5', 
            borderRadius: '6px' 
          }}>
            <Text strong>Summary: </Text>
            <Text>{historyData.length} total values</Text>
            {abnormalCount > 0 && (
              <>
                <Text style={{ marginLeft: 16, color: '#ff4d4f' }}>
                  {abnormalCount} abnormal
                </Text>
              </>
            )}
            {normalCount > 0 && (
              <Text style={{ marginLeft: 16, color: '#52c41a' }}>
                {normalCount} normal
              </Text>
            )}
            <Text style={{ marginLeft: 16 }} type="secondary">
              (Last 12 months)
            </Text>
          </div>

          {/* Table */}
          <Table
            columns={columns}
            dataSource={historyData}
            rowKey={(record) => `${record.entryDate}-${record.value}`}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} results`
            }}
            size="small"
            rowClassName={(record) => 
              record.isAbnormal ? 'abnormal-lab-row' : 'normal-lab-row'
            }
          />

          <style jsx>{`
            .abnormal-lab-row {
              background-color: #fff2f0;
            }
            .normal-lab-row {
              background-color: #f6ffed;
            }
          `}</style>
        </>
      )}
    </Modal>
  );
};

export default LabHistoryModal;
